/**
 * One rule for every identity change (SA-922 spec §1). Whenever the signed-in
 * user changes, everything the previous person left in the browser is cleared
 * at the swap itself:
 * - View-as Start (admin -> target);
 * - its end (target -> admin);
 * - logout and a refresh that failed (A -> null).
 *
 * The subscription never fires on login or boot restore (null -> A), and
 * never on a same-user token refresh. It is installed once, in main.tsx.
 */
import { anamProvider } from '@/connectors/avatar/AnamProvider'
import { tavusProvider } from '@/connectors/avatar/TavusProvider'
import { azureSpeechProvider } from '@/connectors/voice/AzureSpeechProvider'
import { elevenLabsProvider } from '@/connectors/voice/ElevenLabsProvider'
import { resetConversationListRequest } from '@/hooks/useConversations'
import { restartInboxEventStream } from '@/services/inboxEventBus'
import { queryClient } from '@/services/queryClient'
import { getRagConnector, clearSessionConnectorKeys } from '@/services/ragConnector'
import { stopSettingsSync } from '@/services/settingsSync'
import { useAuthStore } from '@/stores/authStore'
import { OWN_RESET, RESET_TO_INITIAL } from '@/stores/identityScope'

/** The singletons `useAppInit` initializes with a key; `destroy()` drops it. */
const PROVIDERS: Array<[string, { destroy: () => Promise<void> }]> = [
  ['elevenLabs', elevenLabsProvider],
  ['azureSpeech', azureSpeechProvider],
  ['tavus', tavusProvider],
  ['anam', anamProvider],
]

/** One step must never stop the rest: this runs inside authStore's `set()`,
 * so a throw would also escape from the caller's swap. */
function attempt(step: string, run: () => void): void {
  try {
    run()
  } catch (error) {
    console.warn(`[identityChange] ${step} failed:`, error)
  }
}

export function clearIdentityState(): void {
  // First: the sync must not save a store we are about to reset, to the new
  // account.
  attempt('settings sync', stopSettingsSync)
  attempt('query cache', () => queryClient.clear())
  for (const [path, store] of Object.entries(RESET_TO_INITIAL)) {
    attempt(path, () => store.setState(store.getInitialState(), true))
  }
  for (const [path, reset] of Object.entries(OWN_RESET)) attempt(path, reset)
  // Module state, not a store: whose conversation list was requested (SA-972).
  attempt('conversation list request', resetConversationListRequest)
  for (const [name, provider] of PROVIDERS) {
    attempt(`${name} provider`, () => {
      void provider.destroy().catch((error: unknown) => console.warn(`[identityChange] ${name} provider destroy failed:`, error))
    })
  }
  // The connector holds the key outside any store.
  attempt('RAG key', () => getRagConnector().setApiKey(''))
  attempt('RAG session keys', clearSessionConnectorKeys)
  attempt('inbox stream', restartInboxEventStream)
}

/** Subscribe once; returns the unsubscribe. */
export function installIdentityChangeReset(): () => void {
  return useAuthStore.subscribe((state, previous) => {
    const before = previous.user?.id ?? null
    const after = state.user?.id ?? null
    if (before !== null && after !== before) clearIdentityState()
  })
}
